import * as React from "react";
import { useEffect, useState } from "react";
import { makeStyles, Text, tokens } from "@fluentui/react-components";
import { NewformaSwitch } from "../../_base/NewformaSwitch";
import { NewformaSpinButton } from "../../_base/NewformaSpinButton";
import { DateHelper } from "../../../helpers/DateHelpers";

export const MIN_REMINDER_DAYS = 1;
export const DEFAULT_MAX_REMINDER_DAYS = 365;

export function getCurrentLocalDay(): Date {
    const now = DateHelper.TodayDate;
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function useCurrentLocalDay(): Date {
    const [currentLocalDay, setCurrentLocalDay] = useState(getCurrentLocalDay);

    useEffect(() => {
        const now = DateHelper.TodayDate;
        const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const timeoutId = setTimeout(() => {
            // Refreshes DateHelper's own cached snapshot too, so a pane left open across midnight
            // doesn't keep reading yesterday from TodayDate elsewhere in the app.
            DateHelper.synchNow();
            setCurrentLocalDay(getCurrentLocalDay());
        }, nextMidnight.getTime() - now.getTime());

        return () => clearTimeout(timeoutId);
    }, [currentLocalDay]);

    return currentLocalDay;
}

// A reminder on the same day the request is sent out isn't required, so the latest valid "days
// before due" excludes that day: e.g. today Monday / due Friday allows 1-3 days.
export function getMaxReminderDays(
    dueDate: Date | undefined,
    cap: number = DEFAULT_MAX_REMINDER_DAYS,
    today: Date = new Date()
): number {
    if (!dueDate) {
        return cap;
    }
    const daysUntilDue = DateHelper.subDate(today, dueDate) - 1;
    return Math.min(cap, Math.max(0, daysUntilDue));
}

const useStyles = makeStyles({
    // Two equal columns: toggle on the left, day stepper (when shown) on the right.
    row: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        columnGap: tokens.spacingHorizontalL,
        alignItems: "center",
    },
    // Overrides NewformaSwitch's 12px default for this form: 14px / regular / 20px, and lets the
    // label wrap onto a second line in a narrow pane instead of overflowing its grid cell.
    switch: {
        minWidth: 0,
        "& .fui-Switch__label": {
            fontSize: tokens.fontSizeBase300,
            fontWeight: tokens.fontWeightRegular,
            lineHeight: tokens.lineHeightBase300,
            whiteSpace: "normal",
            minWidth: 0,
        },
    },
    days: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalXS,
        minWidth: 0,
    },
    spinButton: {
        boxSizing: "border-box",
        minWidth: 0,
        gridTemplateColumns: "minmax(0, 1fr) 24px",
        "& .fui-SpinButton__input": {
            minWidth: 0,
            width: "100%",
            fontSize: tokens.fontSizeBase300,
            fontWeight: tokens.fontWeightRegular,
            lineHeight: tokens.lineHeightBase300,
        },
    },
    daysLabel: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        lineHeight: tokens.lineHeightBase300,
        // wraps onto a second line when the pane is narrow rather than pushing the row wider
        whiteSpace: "normal",
        minWidth: 0,
    },
});

export interface ReminderFluent9Props {
    /** Prefixes every data-testid: `${testIdPrefix}-row`, `-switch`, `-days`, `-days-stepper`. */
    testIdPrefix: string;
    label: string;
    daysLabel: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    switchDisabled?: boolean;
    days: number;
    onDaysChange: (days: number) => void;
    min: number;
    max: number;
    stepperDisabled?: boolean;
}

// Shared "turn on a reminder N days before due" control: mirrors the legacy shared ReminderComponent
// (toggle plus a day stepper that only appears once the toggle is on), used by both the main RFI
// page and the Forward for Review page.
export const ReminderFluent9: React.FC<ReminderFluent9Props> = (props) => {
    const styles = useStyles();

    return (
        <div className={styles.row} data-testid={`${props.testIdPrefix}-row`}>
            <NewformaSwitch
                dataTestId={`${props.testIdPrefix}-switch`}
                className={styles.switch}
                label={props.label}
                checked={props.checked}
                onChange={(_e, data) => props.onCheckedChange(!!data.checked)}
                disabled={props.switchDisabled}
            />
            <div className={styles.days} data-testid={`${props.testIdPrefix}-days`}>
                {props.checked ? (
                    <>
                        <NewformaSpinButton
                            dataTestId={`${props.testIdPrefix}-days-stepper`}
                            className={styles.spinButton}
                            value={props.days}
                            min={props.min}
                            max={props.max}
                            step={1}
                            onChange={props.onDaysChange}
                            disabled={props.stepperDisabled}
                            aria-label={props.daysLabel}
                        />
                        <Text className={styles.daysLabel}>{props.daysLabel}</Text>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default ReminderFluent9;
